import { useState, useEffect } from 'react';
import { FiFileText, FiMail, FiClipboard, FiUsers, FiBarChart2 } from 'react-icons/fi';
import { blogService, newsletterService, universityService, studentService, analyticsService } from '../services/apiService';
import { getImageSrc } from '../utils/imageHelper';
import SccnLogo from "../assets/images/SCCNlogo.png";
import { useAuth } from '../context/AuthContext';
import React from 'react';

const Dashboard = () => {
  const { admin } = useAuth();
  const [stats, setStats] = useState({
    blogs: 0,
    simplySmart: 0,
    globallySmart: 0,
    pendingRequests: 0,
    approvedRequests: 0,
    totalUniversities: 0,
    totalStudents: 0,
  });
  const [loading, setLoading] = useState(true);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [performanceData, setPerformanceData] = useState({
    days: [],
    blogViews: [],
    newsletterOpens: [],
    totalBlogViews: 0,
    totalNewsletterOpens: 0,
  });
  const [timeRange, setTimeRange] = useState('weekly');
  const [error, setError] = useState(null);
  const [analyticsAvailable, setAnalyticsAvailable] = useState(false);

  useEffect(() => {
    if (!admin) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch blogs data (paginated) - avoid fetching entire blog list
        let blogsCount = 0;
        let blogsData = [];
        try {
          const blogsPageRes = await blogService.getBlogs({ page: 1, limit: 3 });
          if (blogsPageRes && blogsPageRes.data) {
            blogsData = blogsPageRes.data || [];
            blogsCount = blogsPageRes.pagination?.totalItems ?? (Array.isArray(blogsPageRes.data) ? blogsPageRes.data.length : 0);
          } else if (Array.isArray(blogsPageRes)) {
            // backward compatibility if service returns array
            blogsData = blogsPageRes.slice(0, 3);
            blogsCount = blogsPageRes.length;
          }
        } catch (blogsErr) {
          console.warn("Could not fetch blogs:", blogsErr.message);
        }

        // Fetch newsletters counts by type (paginated) - avoid fetching all newsletter entries
        let simplySmartCount = 0;
        let globallySmartCount = 0;
        try {
          const simplyRes = await newsletterService.getNewslettersByType('simply-smart', { page: 1, limit: 1 });
          const globallyRes = await newsletterService.getNewslettersByType('globally-smart', { page: 1, limit: 1 });

          if (simplyRes && simplyRes.pagination) {
            simplySmartCount = simplyRes.pagination.totalItems || 0;
          } else if (simplyRes && Array.isArray(simplyRes.data)) {
            simplySmartCount = simplyRes.data.length;
          }

          if (globallyRes && globallyRes.pagination) {
            globallySmartCount = globallyRes.pagination.totalItems || 0;
          } else if (globallyRes && Array.isArray(globallyRes.data)) {
            globallySmartCount = globallyRes.data.length;
          }
        } catch (newslettersErr) {
          console.warn("Could not fetch newsletters:", newslettersErr.message);
        }

        // Fetch university requests
        let pendingRequestsCount = 0;
        let approvedRequestsCount = 0;
        let totalUniversitiesCount = 0;
        let requestsData = [];
        try {
          // Fetch summary/count endpoint instead of all data
          const allRequestsRes = await universityService.getAllUniversities({
            status: 'all',
            page: 1,
            limit: 5 // Only need recent few for display
          });
          
          if (allRequestsRes && allRequestsRes.pagination) {
            totalUniversitiesCount = allRequestsRes.pagination.totalItems || 0;
            // Use pagination stats for counts - no need to filter all data
            pendingRequestsCount = allRequestsRes.pagination.totalPending || 0;
            approvedRequestsCount = allRequestsRes.pagination.totalApproved || 0;
            // For recent display, use limited data
            if (allRequestsRes.data) {
              requestsData = allRequestsRes.data.slice(0, 5);
            }
          } else if (allRequestsRes && allRequestsRes.data) {
            pendingRequestsCount = allRequestsRes.data.filter(r => !r.isVerified && !r.rejectionReason).length;
            approvedRequestsCount = allRequestsRes.data.filter(r => r.isVerified).length;
            totalUniversitiesCount = allRequestsRes.data.length;
          }
          
          // Fetch recent 3 for display (can be pending or any status)
          const requestsRes = await universityService.getAllUniversities({
            status: 'all',
            limit: 3,
            sortBy: 'createdAt',
            sortOrder: 'desc'
          });
          if (requestsRes && requestsRes.data) {
            requestsData = Array.isArray(requestsRes.data) ? requestsRes.data.slice(0, 3) : [];
          }
        } catch (requestsErr) {
          console.warn("Could not fetch registration requests:", requestsErr.message);
        }

        // Fetch total students count
        let totalStudentsCount = 0;
        try {
          const studentsRes = await studentService.getAllStudents({ page: 1, limit: 1 });
          if (studentsRes && studentsRes.pagination) {
            totalStudentsCount = studentsRes.pagination.totalItems || 0;
          } else if (studentsRes && studentsRes.data) {
            totalStudentsCount = Array.isArray(studentsRes.data) ? studentsRes.data.length : 0;
          }
        } catch (studentsErr) {
          console.warn("Could not fetch students:", studentsErr.message);
        }

        // Fetch performance data from backend analytics
        try {
          const performanceRes = await analyticsService.getContentPerformance({
            range: timeRange
          });
          
          if (performanceRes && 
              Array.isArray(performanceRes.days) && 
              Array.isArray(performanceRes.blogViews) && 
              Array.isArray(performanceRes.newsletterOpens)) {
            
            setPerformanceData({
              days: performanceRes.days || [],
              blogViews: performanceRes.blogViews || [],
              newsletterOpens: performanceRes.newsletterOpens || [],
              totalBlogViews: performanceRes.totalBlogViews || 0,
              totalNewsletterOpens: performanceRes.totalNewsletterOpens || 0
            });
            setAnalyticsAvailable(true);
          } else {
            console.warn("Performance data structure invalid");
            setAnalyticsAvailable(false);
            // Initialize empty performance data
            setPerformanceData({
              days: [],
              blogViews: [],
              newsletterOpens: [],
              totalBlogViews: 0,
              totalNewsletterOpens: 0
            });
          }
        } catch (perfErr) {
          console.warn("Could not fetch performance data:", perfErr.message);
          setAnalyticsAvailable(false);
          // Initialize empty performance data on error
          setPerformanceData({
            days: [],
            blogViews: [],
            newsletterOpens: [],
            totalBlogViews: 0,
            totalNewsletterOpens: 0
          });
        }

        setStats({
          blogs: blogsCount,
          simplySmart: simplySmartCount,
          globallySmart: globallySmartCount,
          pendingRequests: pendingRequestsCount,
          approvedRequests: approvedRequestsCount,
          totalUniversities: totalUniversitiesCount,
          totalStudents: totalStudentsCount,
        });

        setRecentBlogs(blogsData);
        setRecentRequests(requestsData);
      } catch (err) {
        console.error("Failed to fetch dashboard data", err);
        setError("Failed to load dashboard data. Some information may not be current.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [admin, timeRange]);

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  // StatCard component
  const StatCard = ({ icon, title, value, color, link }) => (
    <div className={`bg-white rounded-lg shadow-md p-4 transition-all duration-300 hover:shadow-lg border-l-4 ${color} w-full`} style={{ minWidth: '180px' }}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="text-gray-500 text-xs font-medium">{title}</div>
          <div className="text-xl font-medium mt-1 text-gray-800">{loading ? "..." : value.toLocaleString()}</div>
        </div>
        <div className="p-2 rounded-lg ml-2" style={{ backgroundColor: color.replace('border-', 'bg-').split('-') + '-100' }}>
          {React.cloneElement(icon, { className: "text-lg " + icon.props.className })}
        </div>
      </div>
      <a href={link} className="mt-2 inline-flex items-center text-xs font-medium text-[#0e3d1f] hover:text-[#166534] transition-colors">
        View details
        <svg className="ml-1 w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
        </svg>
      </a>
    </div>
  );

  // ActivityItem component
  const ActivityItem = ({ icon, title, description, time }) => (
    <div className="flex items-start py-2">
      <div className="mr-3 text-lg text-[#0e3d1f]">{icon}</div>
      <div className="flex-1">
        <div className="font-medium text-gray-800 text-sm">{title}</div>
        <div className="text-xs text-gray-500">{description}</div>
        <div className="text-xs text-gray-400 mt-1">{time}</div>
      </div>
    </div>
  );

  // PerformanceChart component
  const PerformanceChart = () => {
    if (loading) {
      return (
        <div className="h-56 flex items-center justify-center">
          <div className="animate-pulse flex space-x-4">
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="h-8 bg-gray-200 rounded w-6"></div>
            ))}
          </div>
        </div>
      );
    }
    
    if (!analyticsAvailable || performanceData.days.length === 0) {
      return (
        <div className="h-56 flex flex-col items-center justify-center text-center p-4">
          <FiBarChart2 className="text-gray-400 text-2xl mb-2" />
          <p className="text-sm text-gray-500 mb-1">Analytics data not available</p>
          <p className="text-xs text-gray-400">
            {performanceData.days.length === 0 
              ? "No data available for the selected time range" 
              : "The analytics endpoint is currently unavailable"
            }
          </p>
        </div>
      );
    }

    const maxValue = Math.max(
      10,
      ...performanceData.blogViews,
      ...performanceData.newsletterOpens
    );

    return (
      <div className="h-56">
        <div className="flex items-end h-40 space-x-1">
          {performanceData.days.map((day, index) => (
            <div key={`${day}-${index}`} className="flex-1 flex flex-col items-center">
              <div className="text-xs text-gray-500 mb-1">{day.length > 3 ? day.substring(0, 3) : day}</div>
              <div className="flex items-end w-full justify-center space-x-1" style={{ height: '100%' }}>
                <div
                  className="w-4 bg-[#0e3d1f] rounded-t transition-all duration-500 ease-out hover:opacity-100"
                  style={{
                    height: `${(performanceData.blogViews[index] / maxValue) * 100}%`,
                    minHeight: performanceData.blogViews[index] > 0 ? '4px' : '0'
                  }}
                  title={`Blog Views: ${performanceData.blogViews[index].toLocaleString()}`}
                ></div>
                <div
                  className="w-4 bg-[#166534] rounded-t transition-all duration-500 ease-out hover:opacity-100"
                  style={{
                    height: `${(performanceData.newsletterOpens[index] / maxValue) * 100}%`,
                    minHeight: performanceData.newsletterOpens[index] > 0 ? '4px' : '0'
                  }}
                  title={`Newsletter Opens: ${performanceData.newsletterOpens[index].toLocaleString()}`}
                ></div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-4">
          <div className="flex items-center">
            <div className="w-2.5 h-2.5 rounded-full bg-[#0e3d1f] mr-2"></div>
            <span className="text-xs text-gray-600">Blog Views ({performanceData.totalBlogViews.toLocaleString()})</span>
          </div>
          <div className="flex items-center">
            <div className="w-2.5 h-2.5 rounded-full bg-[#166534] mr-2"></div>
            <span className="text-xs text-gray-600">Newsletter Opens ({performanceData.totalNewsletterOpens.toLocaleString()})</span>
          </div>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border-l-4 border-red-500 p-3">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-4 w-4 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-2">
              <p className="text-xs text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center mb-6">
        <img src={SccnLogo} alt="SCCN Logo" className="h-10 w-auto mr-3" />
        <div>
          <h1 className="text-2xl font-medium text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">Welcome back! Here's what's happening today.</p>
        </div>
      </div>

      {/* Stats Overview - Row 1 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
        <StatCard icon={<FiFileText className="text-[#0e3d1f]" />} title="Total Blogs" value={stats.blogs} color="border-[#0e3d1f]" link="/blogs" />
        <StatCard icon={<FiMail className="text-[#166534]" />} title="Simply Smart" value={stats.simplySmart} color="border-[#166534]" link="/simply-smart" />
        <StatCard icon={<FiMail className="text-[#0e3d1f]" />} title="Globally Smart" value={stats.globallySmart} color="border-[#0e3d1f]" link="/globally-smart" />
        <StatCard icon={<FiClipboard className="text-[#F59E0B]" />} title="Pending Requests" value={stats.pendingRequests} color="border-[#F59E0B]" link="/university-requests" />
        <StatCard icon={<FiUsers className="text-[#10B981]" />} title="Approved Universities" value={stats.approvedRequests} color="border-[#10B981]" link="/university-requests?status=approved" />
      </div>

      {/* Stats Overview - Row 2 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 mb-6">
        <StatCard icon={<FiUsers className="text-[#3B82F6]" />} title="Total Universities" value={stats.totalUniversities} color="border-[#3B82F6]" link="/universities-list" />
        <StatCard icon={<FiUsers className="text-[#8B5CF6]" />} title="Total Students" value={stats.totalStudents} color="border-[#8B5CF6]" link="/students-list" />
      </div>

      {/* Chart and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Content Performance Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-800">Content Performance</h2>
            <div className="flex space-x-1">
              <button onClick={() => handleTimeRangeChange('weekly')}
                className={`px-2 py-1 text-xs rounded-md ${timeRange === 'weekly' ? 'bg-[#0e3d1f] bg-opacity-10 text-[#0e3d1f]' : 'text-gray-500 hover:bg-gray-100'}`}
              >Weekly</button>
              <button onClick={() => handleTimeRangeChange('monthly')}
                className={`px-2 py-1 text-xs rounded-md ${timeRange === 'monthly' ? 'bg-[#0e3d1f] bg-opacity-10 text-[#0e3d1f]' : 'text-gray-500 hover:bg-gray-100'}`}
              >Monthly</button>
              <button onClick={() => handleTimeRangeChange('yearly')}
                className={`px-2 py-1 text-xs rounded-md ${timeRange === 'yearly' ? 'bg-[#0e3d1f] bg-opacity-10 text-[#0e3d1f]' : 'text-gray-500 hover:bg-gray-100'}`}
              >Yearly</button>
            </div>
          </div>
          <PerformanceChart />
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-800">Recent Activity</h2>
            <button className="text-xs text-[#0e3d1f] hover:text-[#166534]">View all</button>
          </div>
          <div className="space-y-1">
            {recentBlogs.length > 0 && recentBlogs[0] && (
              <ActivityItem 
                icon={<FiFileText />} 
                title="New Blog Published" 
                description={recentBlogs[0].title || "No recent blogs"} 
                time={recentBlogs[0] ? new Date(recentBlogs[0].createdAt || recentBlogs[0].publishDate).toLocaleDateString() : ""} 
              />
            )}
            {recentRequests.length > 0 && recentRequests[0] && (
              <ActivityItem 
                icon={<FiClipboard />} 
                title="University Request" 
                description={recentRequests[0].instituteName || recentRequests[0].university || "No recent requests"} 
                time={recentRequests[0] ? new Date(recentRequests[0].createdAt).toLocaleDateString() : ""} 
              />
            )}
            <ActivityItem icon={<FiMail />} title="Newsletter Sent" description="Simply Smart Newsletter" time="1 day ago" />
            <ActivityItem icon={<FiUsers />} title="New Admin Added" description="New team member joined" time="2 days ago" />
          </div>
        </div>
      </div>

      {/* Recent Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Blogs */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-800">Recent Blogs</h2>
            <a href="/blogs" className="text-xs text-[#0e3d1f] hover:text-[#166534]">View all</a>
          </div>
          <div className="space-y-3">
            {loading ? (
              <div className="text-center py-4 text-sm">Loading blogs...</div>
            ) : recentBlogs.length === 0 ? (
              <div className="text-center py-4 text-sm">No blogs found</div>
            ) : recentBlogs.map(blog => (
              <div key={blog._id} className="flex items-start py-2 border-b border-gray-100 last:border-0 group">
                <div className="mr-3 flex-shrink-0">
                  {blog.thumbnail ? (
                    <img src={getImageSrc(blog.thumbnail)}
                      alt={blog.title}
                      className="w-12 h-12 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/48";
                      }} />
                  ) : (
                    <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                      <FiFileText className="text-gray-400 text-lg" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm text-gray-900 group-hover:text-[#0e3d1f] transition-colors">{blog.title}</h3>
                  <div className="flex items-center text-xs text-gray-500 mt-1">
                    <span>{blog.author || "Unknown Author"}</span>
                    <span className="mx-1">•</span>
                    <span>{new Date(blog.createdAt || blog.publishDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex mt-1 flex-wrap">
                    {blog.tags?.slice(0, 3).map((tag, i) => (
                      <span key={i} className="mr-1 mb-1 px-1.5 py-0.5 text-2xs rounded-full bg-[#166534] bg-opacity-10 text-[#0e3d1f]">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent University Requests */}
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-800">Recent University Requests</h2>
            <a href="/university-requests" className="text-xs text-[#0e3d1f] hover:text-[#166534]">View all</a>
          </div>
          <div className="space-y-3">
            {loading ? (
              <div className="text-center py-4 text-sm">Loading requests...</div>
            ) : recentRequests.length === 0 ? (
              <div className="text-center py-4 text-sm">No requests found</div>
            ) : recentRequests.map(request => (
              <div key={request._id} className="flex items-center py-2 border-b border-gray-100 last:border-0 group">
                <div className="mr-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${
                    request.isVerified ? 'bg-[#10B981]' :
                    request.rejectionReason ? 'bg-red-500' : 'bg-[#F59E0B]'
                  }`}></div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm text-gray-900 group-hover:text-[#0e3d1f] transition-colors">{request.instituteName || request.university || 'Unknown University'}</h3>
                  <div className="flex items-center text-xs text-gray-500 mt-1">
                    <span>{request.contactPerson || 'Unknown contact'}</span>
                    <span className="mx-1">•</span>
                    <span>{request.contactEmail || 'No email'}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Submitted on {new Date(request.createdAt).toLocaleDateString()}</div>
                  {request.rejectionReason && (
                    <div className="text-2xs text-red-500 mt-1">Rejected: {request.rejectionReason}</div>
                  )}
                </div>
                <div>
                  <span className={`px-1.5 py-0.5 text-2xs rounded-full ${
                    request.isVerified ? 'bg-[#10B981] bg-opacity-10 text-[#10B981]' :
                    request.rejectionReason ? 'bg-red-500 bg-opacity-10 text-red-500' :
                    'bg-[#F59E0B] bg-opacity-10 text-[#F59E0B]'
                  }`}>
                    {request.isVerified ? 'Approved' : request.rejectionReason ? 'Rejected' : 'Pending'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;